import { BannerMode } from "@/types/BannerMode.interface";
import useUser from "@/users/useUser";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import contentTypes from "@/view/constants/contentTypes";
import { IFRAME_CONTENT_TYPES } from "@/view/constants/contentTypes/contentTypes.constants";
import { DISABLED_FEATURES } from "@/view/SecondaryActions/SecondaryActions";
import SecondaryActionsToolbar from "@/view/SecondaryActionsToolbar";
import UnlevelledContentStandardsRow from "@/view/UnlevelledContent/UnlevelledContentStandardsRow";
import getBestMatchingMaturityBands from "@/view/utils/getBestMatchingMaturityBands";
import { getMaturityRange } from "@/view/utils/getBestMatchingMaturityBands/getBestMatchingMaturityBands";
import GenerationGeniusLogo from "./GenerationGeniusLogo";
import {
  headerCss,
  labelCss,
  logoCss,
  metadataContainerCss,
  metadataCss,
  metadataItemCss,
  titleCss,
  valueCss,
} from "./UnlevelledContentHeader.style";

import type { IUnlevelledContent } from "@/types/models/UnlevelledContent.interface";

interface UnlevelledContentHeaderProps {
  content: IUnlevelledContent;
  duration?: string;
  bannerMode: BannerMode;
}

const UnlevelledContentHeader = ({
  content,
  duration,
  bannerMode,
}: UnlevelledContentHeaderProps) => {
  const contentData = content.data;

  const isGenerationGenius =
    content?.content_type === contentTypes.GENERATION_GENIUS_VIDEO;
  const isIFrameContent = IFRAME_CONTENT_TYPES.includes(content?.content_type);

  const { title, maturity_range: maturityRange } = contentData;

  const author = isGenerationGenius
    ? "Generation Genius"
    : contentData?.author || "Unknown Author";

  const getRecommendedValue = (maturityRange: number[]) => {
    if (maturityRange) {
      const matchingMaturityBands = getBestMatchingMaturityBands(maturityRange);
      return getMaturityRange(matchingMaturityBands);
    }

    return "";
  };

  const recommendedFor = getRecommendedValue(maturityRange);
  const metadata = {
    by: author,
    "Recommended For": recommendedFor,
    duration: isGenerationGenius ? duration : undefined,
  };

  const { data: user } = useUser();
  const { isTeacher = false } = useGetUserProperties();

  const standards = content.page_viewmodel?.standards || [];

  let disableFeatures: DISABLED_FEATURES[] = [
    "add-to-text-set",
    "listen-mode",
    "paragraph-count",
    "present-mode",
    "translation",
  ];

  if (isIFrameContent) {
    disableFeatures = [...disableFeatures, "font-size-action"];
  }

  const shouldShowStandards = isTeacher && standards.length > 0;

  return (
    <div css={headerCss}>
      {isGenerationGenius && <GenerationGeniusLogo css={logoCss} />}
      <h1 css={titleCss}>{title}</h1>
      <div css={metadataContainerCss}>
        <div css={metadataCss}>
          {Object.entries(metadata).map(([label, value]) => {
            if (label !== "Recommended For" && value) {
              return (
                <div css={metadataItemCss} key={label}>
                  <span css={labelCss}>{label}</span>
                  <span css={valueCss}>{value}</span>
                </div>
              );
            }
            return null;
          })}
        </div>
        {recommendedFor && (
          <div css={metadataCss}>
            <div css={metadataItemCss}>
              <span css={labelCss}>Recommended For</span>
              <span css={valueCss}>{recommendedFor}</span>
            </div>
          </div>
        )}
        {/* Standards - only visible to teachers */}
        {shouldShowStandards && (
          <UnlevelledContentStandardsRow
            standards={standards}
            content={content}
          />
        )}
      </div>
      <SecondaryActionsToolbar
        bannerMode={bannerMode}
        content={{
          ...content,
          data: {
            ...contentData,
            article_type: contentTypes.GENERATION_GENIUS_VIDEO,
            content_id: content.id,
            thumbnail: contentData.thumbnail || "",
          },
        }}
        matchedAssignments={[]}
        user={user}
        disabledFeatures={disableFeatures}
        currentLevel={undefined}
        readAloudButtonRef={undefined}
      />
    </div>
  );
};

export default UnlevelledContentHeader;
